// ============================================================
// imageUpload.ts — Supabase Storage helpers + optional client upload
//
// PRIMARY UPLOAD: Use POST /api/upload/product-image (server). Auth, validation,
// and logging live there; ProductFormModal already uses it.
//
// THIS MODULE:
//   - Helpers: isStorageUrl(), isBase64(), extractPathFromUrl() — use when
//     displaying or cleaning product.images[].
//   - deleteProductImage(path) — use when removing an image from Storage (e.g.
//     "remove image" that should delete the object, not only the DB reference).
//   - uploadProductImage(file, onProgress?) — optional client-direct upload
//     (e.g. if you need progress UX or server upload is unavailable). Requires
//     VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.
//
// SETUP: Run 20250222130000_master_sql_v2.sql (creates 'product-images' bucket).
// ============================================================

import { getProductImageUrl, type ProductImageSize } from './productImageUrl';

export type { ProductImageSize };

const BUCKET = 'product-images';

/** Max upload size in bytes. Must match API and Supabase bucket file_size_limit. */
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const MAX_IMAGE_SIZE_MB = MAX_IMAGE_SIZE_BYTES / (1024 * 1024);
const MAX_SIZE_BYTES = MAX_IMAGE_SIZE_BYTES;

/** Vite env for Supabase (this app is Vite-only; no process.env). */
function getEnv(): { VITE_SUPABASE_URL?: string; VITE_SUPABASE_ANON_KEY?: string } {
  if (typeof import.meta !== 'undefined' && import.meta.env != null) {
    return import.meta.env as { VITE_SUPABASE_URL?: string; VITE_SUPABASE_ANON_KEY?: string };
  }
  return {};
}

function getSupabaseUrl(): string {
  const env = getEnv();
  return env.VITE_SUPABASE_URL ?? '';
}

function getSupabaseAnonKey(): string {
  const env = getEnv();
  return env.VITE_SUPABASE_ANON_KEY ?? '';
}

function generatePath(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `products/${ts}-${rand}.${ext}`;
}

/** Max dimension (width or height) for compressed product images. */
const MAX_DIMENSION = 1920;

/**
 * Compress image to stay under maxBytes using Canvas. Preserves aspect ratio.
 * Returns original file if already under limit or not a supported image.
 * Output is always JPEG for smaller size. Uses no dependencies.
 */
async function compressImageIfNeeded(
  file: File,
  maxBytes: number
): Promise<File> {
  if (file.size <= maxBytes) return file;
  if (!file.type.startsWith('image/')) return file;

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = document.createElement('img');
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to decode image'));
      el.src = url;
    });

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;

    let maxDim = MAX_DIMENSION;
    const qualities = [0.92, 0.85, 0.75, 0.6, 0.5];

    for (const q of qualities) {
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);

      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(img, 0, 0, cw, ch);

      const blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob(res, 'image/jpeg', q)
      );
      if (!blob) continue;
      if (blob.size <= maxBytes) {
        const name = file.name.replace(/\.[a-z]+$/i, '.jpg');
        return new File([blob], name, { type: 'image/jpeg' });
      }
      maxDim = Math.round(maxDim * 0.85);
    }
  } finally {
    URL.revokeObjectURL(url);
  }

  return file;
}

export interface UploadResult {
  url: string;
  path: string;
}

/**
 * Upload a product image to Supabase Storage.
 * Large images are compressed client-side to stay under the size limit.
 * Returns { url, path } on success.
 * Throws on failure.
 *
 * @param file - The File object from <input type="file">
 * @param onProgress - Optional progress callback (0-100)
 */
export async function uploadProductImage(
  file: File,
  onProgress?: (pct: number) => void
): Promise<UploadResult> {
  const supabaseUrl = getSupabaseUrl();
  const anonKey = getSupabaseAnonKey();

  if (!supabaseUrl || !anonKey) {
    throw new Error(
      'Supabase not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env'
    );
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image (JPG, PNG, WebP).');
  }

  onProgress?.(5);
  const toUpload = await compressImageIfNeeded(file, MAX_SIZE_BYTES);
  onProgress?.(15);

  if (toUpload.size > MAX_SIZE_BYTES) {
    throw new Error(
      `Image is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max ${MAX_SIZE_BYTES / 1024 / 1024}MB. Compress or resize the image and try again.`
    );
  }

  const path = generatePath(toUpload);
  const uploadUrl = `${supabaseUrl}/storage/v1/object/${BUCKET}/${path}`;

  let authToken = '';
  try {
    authToken =
      localStorage.getItem('auth_token') ??
      localStorage.getItem('access_token') ??
      localStorage.getItem('token') ??
      '';
  } catch {
    /* localStorage not available */
  }

  onProgress?.(10);

  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: authToken ? `Bearer ${authToken}` : `Bearer ${anonKey}`,
      'Content-Type': toUpload.type,
      'Cache-Control': '3600',
    },
    body: toUpload,
  });

  onProgress?.(80);

  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = await res.json();
      msg = (body as { message?: string; error?: string }).message ?? (body as { message?: string; error?: string }).error ?? msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }

  onProgress?.(100);

  const publicUrl = `${supabaseUrl}/storage/v1/object/public/${BUCKET}/${path}`;
  return { url: publicUrl, path };
}

export async function deleteProductImage(path: string): Promise<void> {
  const supabaseUrl = getSupabaseUrl();
  const anonKey = getSupabaseAnonKey();
  if (!supabaseUrl || !anonKey) return;

  let authToken = '';
  try {
    authToken =
      localStorage.getItem('auth_token') ??
      localStorage.getItem('access_token') ??
      localStorage.getItem('token') ??
      '';
  } catch {
    /* ok */
  }

  await fetch(`${supabaseUrl}/storage/v1/object/${BUCKET}/${path}`, {
    method: 'DELETE',
    headers: {
      apikey: anonKey,
      Authorization: authToken ? `Bearer ${authToken}` : `Bearer ${anonKey}`,
    },
  });
}

export function extractPathFromUrl(url: string): string | null {
  try {
    const match = url.match(/\/object\/public\/product-images\/(.+)$/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

export function isStorageUrl(src: string): boolean {
  return src.startsWith('http') && src.includes('/storage/v1/object/');
}

export function isBase64(src: string): boolean {
  return src.startsWith('data:');
}

/** Allowed path for our bucket. Only URLs from our Supabase origin + this path are allowed. */
const STORAGE_OBJECT_PATH = '/storage/v1/object/';

/** Public object URLs for the product-images bucket (any Supabase project host). */
const STORAGE_PUBLIC_PRODUCT_IMAGES = /\/storage\/v1\/object\/public\/product-images\//i;

/** Image Transform render URLs (fallback target on load error). */
const STORAGE_RENDER_PRODUCT_IMAGES = /\/storage\/v1\/render\/image\/public\/product-images\//i;

/**
 * True when URL is our Supabase Storage product-images path (https only for http URLs).
 */
export function isAllowedProductStorageUrl(src: string): boolean {
  const s = src.trim();
  if (!s.startsWith('https://')) return false;
  return STORAGE_PUBLIC_PRODUCT_IMAGES.test(s) || STORAGE_RENDER_PRODUCT_IMAGES.test(s);
}

/**
 * Strip Image Transform path back to a plain public object URL (works without Pro transforms).
 */
export function productImageUrlWithoutTransform(url: string): string {
  return url.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
}

/**
 * Returns a URL safe to use as img src: only our Supabase Storage (product-images) or data: base64.
 * Prevents XSS from arbitrary user-supplied URLs. For invalid URLs returns a 1x1 transparent GIF.
 *
 * Does not require VITE_SUPABASE_URL to match — list/API may return Storage URLs while env is unset.
 */
export function safeProductImageUrl(src: string): string {
  if (typeof src !== 'string' || !src) return EMPTY_IMAGE_DATA_URL;
  const s = src.trim();
  if (isBase64(s)) return s;
  if (isAllowedProductStorageUrl(s)) return productImageUrlWithoutTransform(s);
  const base = getSupabaseUrl().replace(/\/$/, '');
  if (base && s.startsWith(base) && s.includes(STORAGE_OBJECT_PATH) && s.includes(BUCKET)) {
    return s;
  }
  return EMPTY_IMAGE_DATA_URL;
}

/** 1x1 transparent GIF used when image URL is not allowed (XSS). */
export const EMPTY_IMAGE_DATA_URL =
  'data:image/gif;base64,R0lGOODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * XSS-safe display URL with optional Supabase Image Transform (thumb/medium/full).
 * Use for POS and inventory lists. Data URLs and allowed Storage URLs only.
 */
export function getSafeProductImageUrlSized(
  src: string,
  size: ProductImageSize = 'thumb'
): string {
  const safe = safeProductImageUrl(src);
  if (safe === EMPTY_IMAGE_DATA_URL) return safe;
  return getProductImageUrl(safe, size);
}
